/*
 * Purpose: Autopilot progress — single loading surface while pipeline runs
 * Last modified: 2026-06-28 | Agent
 * Completeness: 92
 */
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import type { AutoBlocksPhase } from '@/services/autoblocks/types';

const STAGE_LABELS: Partial<Record<AutoBlocksPhase, string>> = {
  interpreting: 'Understanding your outcome…',
  confirm: 'Confirming intent…',
  express_running: 'Planning your flow…',
  director: 'Director wiring your Mission…',
  planning: 'Building your plan…',
  review: 'Almost ready…',
  dry_run: 'Simulating dry run…',
};

const STAGE_PROGRESS: Partial<Record<AutoBlocksPhase, number>> = {
  interpreting: 20,
  confirm: 35,
  express_running: 50,
  director: 60,
  planning: 70,
  review: 85,
  dry_run: 95,
};

interface AutopilotProgressCardProps {
  phase: AutoBlocksPhase;
  isDryRunning: boolean;
}

export function AutopilotProgressCard({ phase, isDryRunning }: AutopilotProgressCardProps) {
  const activePhase = isDryRunning ? 'dry_run' : phase;
  const label = STAGE_LABELS[activePhase] ?? 'Preparing your plan…';
  const progress = STAGE_PROGRESS[activePhase] ?? 15;

  return (
    <Card className="border-0 shadow-none ring-1 ring-primary/15">
      <CardContent className="py-10 px-6 flex flex-col items-center gap-4 text-center">
        <div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-foreground">{label}</p>
          <p className="text-xs text-muted-foreground max-w-sm">
            We interpret, plan, and dry-run automatically — you only review the final plan and unlock connections.
          </p>
        </div>
        <Progress value={progress} className="h-1.5 w-full max-w-xs" />
      </CardContent>
    </Card>
  );
}
